import { useEffect, useRef, useState } from 'react';
import { useParams } from 'next/navigation';
import { buildQueryString } from '@/utils/buildQueryString';
import updateFilterOptions from '@/utils/college-listing/updateFilterOptions';
import updateStreams from '@/utils/college-listing/updateStreams';

import apiRequest from '@/lib/api/apiHelper';

import ListingPageDesktopFilterComponent from './ListingPageDesktopFilterComponent';

// import ListingPageMobileFilter from './ListingPageMobileFilter';

const getInfoFromUrl = async (colleges: any, filterData: any) => {
  if (!colleges || colleges.length === 0)
    return {
      location: null,
      streamInfo: null,
      courseInfo: null,
      specializationInfo: null,
      specialData: null,
    };

  const streamsArray = [
    ...new Map(
      filterData
        .flatMap((college: { streams: any }) => college.streams)
        .map((streamInfo: { id: any }) => [streamInfo.id, streamInfo]),
    ).values(),
  ];

  const courseArray = [
    ...new Map(
      filterData
        .flatMap((college: { course: any }) => college.course)
        .map((courseInfo: { id: any }) => [courseInfo.id, courseInfo]),
    ).values(),
  ];

  const specializationArray = [
    ...new Map(
      filterData
        .flatMap((college: { specializations: any }) => college.specializations)
        .map((specializationInfo: { id: any }) => [specializationInfo?.id, specializationInfo]),
    ).values(),
  ];
  const lastValue = colleges[colleges.length - 1];
  const generalRegex = /^(colleges-in-[a-z0-9-]+)$|^([a-z0-9-]+)-colleges-in-([a-z0-9-]+)$/;
  const generalMatch = generalRegex.exec(lastValue);

  let location = null;
  let streamInfo = null;
  let courseInfo: any = null;
  let specializationInfo = null;
  let specialData = null;

  if (generalMatch) {
    if (generalMatch[1]) {
      location = generalMatch[1].replace('colleges-in-', '');
    } else if (generalMatch[2] && generalMatch[3]) {
      location = generalMatch[3];

      // Check if generalMatch[2] exists in streamsArray
      streamInfo =
        streamsArray.find(
          (stream: any) => stream.stream_name.toLowerCase() === generalMatch[2].replace(/-/g, ' ').toLowerCase(),
        ) ?? null;

      // Check if generalMatch[2] exists in courseArray
      courseInfo = courseArray.find((course: any) => course.course_url === generalMatch[2]) ?? null;

      specializationInfo =
        specializationArray.find(
          (specializations: any) =>
            specializations?.specialization_url?.toLowerCase() === generalMatch[2]?.toLowerCase(),
        ) ?? null;

      if (courseInfo) {
        const courseQueryParams = {
          fields: ['abbreviation', 'course_name', 'course_url', 'is_top'],
          populate: {
            streams: {
              fields: ['id', 'stream_name', 'stream_url'],
            },
            specializations: {
              fields: ['id', 'specialization_name', 'specialization_url', 'abbreviation'],
            },
          },
          filters: {
            id: {
              $eq: courseInfo?.id,
            },
          },
        };

        try {
          // Await the API response
          const streamData: any = await apiRequest(`/courses?${buildQueryString(courseQueryParams)}`, 'GET');

          streamInfo = streamData?.data?.data?.results[0]?.streams[0] ?? null;
          specialData = streamData?.data?.data?.results[0]?.specializations ?? null;
        } catch (error) {
          console.error('Error fetching stream data:', error);
        }
      }
    }
  }

  return { location, streamInfo, courseInfo, specializationInfo, specialData };
};

const ListingPageFilterComponent = ({ geoLocation, filterData, filterSecStreamData }: any) => {
  const router = useParams();
  const collegesUrl = router?.colleges;

  // State for filters
  const [filterOptions, setFilterOptions] = useState<{
    country: any;
    state: any;
    city: any;
    stream: any;
    course: any;
    specialization: string | null;
  }>({
    country: null,
    state: null,
    city: null,
    stream: null,
    course: null,
    specialization: null,
  });

  const [filterResponse, setFilterResponse] = useState<any>(null);

  // Get all info from URL
  useEffect(() => {
    const fetchData = async () => {
      const responseData = await getInfoFromUrl(collegesUrl, filterData);
      setFilterResponse(responseData);
    };

    if (collegesUrl) {
      fetchData();
    }
  }, [collegesUrl, filterData]);

  const { location, streamInfo, courseInfo, specializationInfo, specialData } = filterResponse ?? {};
  // Ensure useEffect always runs in the same order
  useEffect(() => {
    if (location) {
      updateFilterOptions(location, geoLocation, setFilterOptions);
    }
  }, [location, geoLocation]);

  useEffect(() => {
    setFilterOptions((prev) => {
      // When specialization is selected, ensure we maintain stream and course info
      if (specializationInfo) {
        return {
          ...prev,
          stream: streamInfo ?? prev.stream,
          course: courseInfo ?? prev.course,
          specialization: specializationInfo,
        };
      }
      // When course is selected, ensure we maintain stream info
      if (courseInfo) {
        return {
          ...prev,
          stream: streamInfo ?? prev.stream,
          course: courseInfo,
          specialization: specializationInfo,
        };
      }
      // Default case
      return {
        ...prev,
        stream: streamInfo,
        course: courseInfo,
        specialization: specializationInfo,
      };
    });
  }, [location, geoLocation, streamInfo, courseInfo, specializationInfo]);
  //  END : select and remove all kind of filter values
  const filterPopupRef = useRef<HTMLDivElement>(null);
  // set which popup should open
  const [selectedFilterCategory, setSelectedFilterCategory] = useState<string | null>(null);

  // START : available Streams
  const [availableStreams, setAvailableStreams] = useState<any[]>([]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterPopupRef.current && !filterPopupRef.current.contains(event.target as Node)) {
        setSelectedFilterCategory(null);
      }
    };

    // Only add the listener when a filter popup is open
    if (selectedFilterCategory) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedFilterCategory]);

  useEffect(() => {
    if (filterOptions.country) {
      setAvailableStreams(updateStreams(filterData, filterOptions));
    }
  }, [filterData, filterOptions]);
  // END : available Streams

  // Move the conditional inside the return statement
  if (!location) {
    return <div>No location found</div>; // Or any placeholder instead of returning null
  }

  return (
    <div>
      {/* mobile filter: */}
      {/* <div className="block lg:hidden">
        <ListingPageMobileFilter
          filterOptions={filterOptions}
          selectedFilterCategory={selectedFilterCategory}
          filterData={filterData}
          collegesUrl={collegesUrl}
          location={location}
          availableStreams={availableStreams}
          filterSecStreamData={filterSecStreamData}
        />
      </div> */}
      {/* desktop filter: */}
      <div className="hidden lg:block">
        <ListingPageDesktopFilterComponent
          filterOptions={filterOptions}
          selectedFilterCategory={selectedFilterCategory}
          setSelectedFilterCategory={setSelectedFilterCategory}
          filterData={filterData}
          collegesUrl={collegesUrl}
          location={location}
          availableStreams={availableStreams}
          filterSecStreamData={filterSecStreamData}
          filterSpecialization={specialData}
          filterPopupRef={filterPopupRef} // Pass the ref to the child component
        />
      </div>
    </div>
  );
};

export default ListingPageFilterComponent;
